import { UseFormReturn } from "react-hook-form";
import { IModuleRound, useRound } from "../../../modules/roundHandler";
import SelectEditor from "../../Global/Editors/SelectEditor";
import { IModuleByggnadsdata, IModuleVärmePrimärFjärrRound, IModuleVärmePrimärPump, IModuleVärmePrimärPumpRound } from "../../../types/ModuleDatas";
import NumberEditor from "../../Global/Editors/NumberEditor";
import { nameof } from "../../Global/Utils";
import StaticBox from "../../Global/StaticBox";
import { useGlobalData } from "../../GlobalDataProvider";
import { ModuleTypeId } from "../../../types/DBTables";
import { getBIMHandler } from "../../../modules/Beräkningsmoduler/bimHandler";
import notify from "devextreme/ui/notify";
import { MapTooltip } from "../../../types/globalData";
import BFLNote from "../BFL/BFLNote";

interface IProps {
  module: IModuleRound;
  data: any;
  formHook: UseFormReturn;
  usedAsFXSubsystem?: boolean;
}

const RoundVarmePrimarPump = ({ module, data, formHook, usedAsFXSubsystem }: IProps) => {
  const roundHandler = useRound();
  const globalData = useGlobalData();

  if (!globalData.data) return <></>;

  const watch = formHook.watch;

  try {
    const ärUppdateradText = globalData.data.Värme.VärmepumpEnergistatistik.find((r) => r.Id === "Uppdaterad")?.VärmepumpEnergistatistik;
    const hämtaSchablonText = globalData.data.Värme.VärmepumpEnergistatistik.find((r) => r.Id === "Schablonvärde")?.VärmepumpEnergistatistik;
    const hämtaFöregåendeText = globalData.data.Värme.VärmepumpEnergistatistik.find((r) => r.Id === "Föregående")?.VärmepumpEnergistatistik;
    const uppgiftEnergistatistikForm = watch(nameof<IModuleVärmePrimärPumpRound>("uppgiftEnergistatistik"));
    const harUppdateratDrifttimmarForm = watch(nameof<IModuleVärmePrimärPumpRound>("harUppdateratDrifttimmar")) as string;

    const lastRound = roundHandler.getLastFinishedRound();
    const energistatistikAlternativ = lastRound
      ? globalData.data.Värme.VärmepumpEnergistatistik.map((r) => r.VärmepumpEnergistatistik)
      : globalData.data.Värme.VärmepumpEnergistatistik.filter((r) => r.Id !== "Föregående").map((r) => r.VärmepumpEnergistatistik);

    const getLastFinishedRoundData = <T,>(): T | undefined => {
      const modPreviousRoundModule = roundHandler.getLastFinishedRound()?.data.modules.find((m) => m.id === module.id);
      const modPreviousFjärrVärmePumpData = modPreviousRoundModule?.data as T | undefined;
      return modPreviousFjärrVärmePumpData as T | undefined;
    };

    const uppgiftEnergistatistik = uppgiftEnergistatistikForm === undefined ? "" : uppgiftEnergistatistikForm;
    const harUppdateratDrifttimmar = harUppdateratDrifttimmarForm === undefined ? "" : harUppdateratDrifttimmarForm;

    return (
      <>
        <div className="row ml-0 align-items-end">
          <div className="col-lg-12">
            <SelectEditor
              label="Uppgift om energistatistik"
              formHook={formHook}
              fieldName={nameof<IModuleVärmePrimärPumpRound>("uppgiftEnergistatistik")}
              tooltipId={nameof<MapTooltip>("VärmePrimärPump_uppgiftEnergistatistik")}
              items={[""].concat(energistatistikAlternativ)}
              options={{
                required: "Du måste välja ett alternativ",
                onChange: (e) => {
                  const values = formHook.getValues();
                  const selectedText = values.uppgiftEnergistatistik;
                  if (selectedText === hämtaSchablonText) {
                    const bimHandler = roundHandler.getBimHandler();
                    const verkningsgrad = bimHandler.BIM_Vägledande_VerkninsgradAktuellVärmekälla;

                    const schablonVärmeProd = bimHandler.G13_ÅrligBruttoAnvändningCentralVärme();
                    formHook.setValue(nameof<IModuleVärmePrimärPumpRound>("årligVärmeproduktion"), schablonVärmeProd);
                    //const årligVärmeProd = values[nameof<IModuleVärmePrimärPumpRound>("årligVärmeproduktion")] as number | undefined;
                    const schablonElAnvändning = schablonVärmeProd === undefined ? undefined : schablonVärmeProd / verkningsgrad;
                    formHook.setValue(nameof<IModuleVärmePrimärPumpRound>("årligElAnvändning"), schablonElAnvändning);
                  } else if (selectedText === hämtaFöregåendeText) {
                    const lastYear = getLastFinishedRoundData<IModuleVärmePrimärPump>();
                    if (lastYear) {
                      formHook.setValue(nameof<IModuleVärmePrimärPumpRound>("årligElAnvändning"), lastYear.årligElAnvändning);
                      formHook.setValue(nameof<IModuleVärmePrimärPumpRound>("årligVärmeproduktion"), lastYear.årligVärmeproduktion);
                    } else notify("Det finns ingen data från föregående år");
                  } else {
                    formHook.setValue(nameof<IModuleVärmePrimärPumpRound>("årligElAnvändning"), undefined);
                    formHook.setValue(nameof<IModuleVärmePrimärPumpRound>("årligVärmeproduktion"), undefined);
                  }
                },
              }}
            ></SelectEditor>
          </div>
        </div>
        <div className="row ml-0 align-items-end">
          {/* <div className="col-lg-12">Ange mätvärden för pågående energirondering</div> */}
          <div className="col-lg-6">
            <NumberEditor
              label="Värmepumpens årliga värmeproduktion (kWh). 12 månaders ackumulerat värde."
              fieldName={nameof<IModuleVärmePrimärPumpRound>("årligVärmeproduktion")}
              tooltipId={nameof<MapTooltip>("VärmePrimärPump_årligVärmeproduktion")}
              formHook={formHook}
              isDisabled={uppgiftEnergistatistik === hämtaSchablonText || uppgiftEnergistatistik === hämtaFöregåendeText || uppgiftEnergistatistik === ""}
              showWhenIsDisabled={uppgiftEnergistatistik === hämtaSchablonText || uppgiftEnergistatistik === hämtaFöregåendeText}
              options={{ required: "Du måste ange ett värde" }}
            ></NumberEditor>
          </div>
          <div className="col-lg-6">
            <NumberEditor
              label="Värmepumpens årliga EL användning (kWh). 12 månaders ackumulerat värde."
              fieldName={nameof<IModuleVärmePrimärPumpRound>("årligElAnvändning")}
              tooltipId={nameof<MapTooltip>("VärmePrimärPump_årligElAnvändning")}
              formHook={formHook}
              isDisabled={uppgiftEnergistatistik === hämtaSchablonText || uppgiftEnergistatistik === hämtaFöregåendeText || uppgiftEnergistatistik === ""}
              showWhenIsDisabled={uppgiftEnergistatistik === hämtaSchablonText || uppgiftEnergistatistik === hämtaFöregåendeText}
              options={{ required: "Du måste ange ett värde" }}
            ></NumberEditor>
          </div>

          {uppgiftEnergistatistik !== ärUppdateradText ? (
            <>
              <div className="row ml-0 align-items-end">
                <div className="col-lg-12">
                  <SelectEditor
                    label="Har tillgång till uppdaterade uppgifter om ackumulerade drifttimmar? (Ja / Nej)"
                    formHook={formHook}
                    fieldName={nameof<IModuleVärmePrimärPumpRound>("harUppdateratDrifttimmar")}
                    tooltipId={nameof<MapTooltip>("VärmePrimärPump_harUppdateratDrifttimmar")}
                    items={["", "Ja", "Nej"]}
                    options={{ required: "Du måste välja Ja/Nej" }}
                  ></SelectEditor>
                </div>
              </div>
              {harUppdateratDrifttimmar === "Ja" ? (
                <>
                  <div className="row ml-0 align-items-end">
                    <div className="col-lg-6">
                      <NumberEditor
                        label="Ange ackumulerade timmar med EL tillskott (h). 12 månaders ackumulerat värde."
                        fieldName={nameof<IModuleVärmePrimärPumpRound>("ackumuleradeTimmar")}
                        tooltipId={nameof<MapTooltip>("VärmePrimärPump_ackumuleradeTimmar")}
                        formHook={formHook}
                        options={{ required: "Du måste ange ett värde" }}
                      ></NumberEditor>
                    </div>

                    <div className="col-lg-6">
                      <NumberEditor
                        label="Ange ackumulerade drifttimmar för värmepump (h) 12 månaders ackumulerat värde."
                        fieldName={nameof<IModuleVärmePrimärPumpRound>("ackumuleradeDrifttimmar")}
                        tooltipId={nameof<MapTooltip>("VärmePrimärPump_ackumuleradeDrifttimmar")}
                        formHook={formHook}
                        options={{ required: "Du måste ange ett värde" }}
                      ></NumberEditor>
                    </div>
                  </div>
                </>
              ) : (
                <></>
              )}
            </>
          ) : (
            <></>
          )}
        </div>
        {!usedAsFXSubsystem && <BFLNote formHook={formHook} />}
      </>
    );
  } catch (ex) {
    return <>{ex}</>;
  }
};

export default RoundVarmePrimarPump;
